import type { VoidComponent } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { Translation } from "../../../utils/Translation";
import { keyFromSelector } from "i18next";
import { DestructiveModal } from "../../../components/modal/Modal";
import UserService from "../../../services/UserService";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import type { Group } from "../../../state/group";
import { isInvite, resolveUserOrInviteName } from "../../../utils/userUtil";
import type CustomerId from "../../../state/CustomerId";
import type { Invite } from "../../../state/invite";
import type { User } from "../../../state/user";

type RemoveUserFromGroupModalProps = {
  customerId: CustomerId;
  group: Group;
  user: User | Invite;
};

const RemoveUserFromGroupModal: VoidComponent<RemoveUserFromGroupModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();

  const userFullName = () => resolveUserOrInviteName(isInvite(props.user), props.user);

  return (
    <DestructiveModal
      title={t(($) => $.REMOVE_USER_FROM_GROUP_MODAL_TITLE)}
      confirmLabel={t(($) => $.REMOVE_USER_FROM_GROUP_MODAL_ACTIONS_CONFIRM)}
      confirmLabelSlow={t(($) => $.REMOVE_USER_FROM_GROUP_MODAL_ACTIONS_CONFIRM_SLOW)}
      cancelLabel={t(($) => $.REMOVE_USER_FROM_GROUP_MODAL_ACTIONS_CANCEL)}
      description={
        <Translation
          key={keyFromSelector(($) => $.REMOVE_USER_FROM_GROUP_MODAL_DESCRIPTION)}
          options={{
            userFullName: userFullName(),
            groupName: props.group.name,
          }}
          components={{
            b: (props) => <span class="text-base-semibold text-primary">{props.children}</span>,
          }}
        />
      }
      onConfirm={() => {
        const customerId = props.customerId;
        const groupId = props.group.id;
        const userId = props.user.id;

        return new Promise<void>((resolve, reject) =>
          UserService.removeUserFromGroup(customerId).mutate(
            { groupId, id: userId },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                replace({
                  content: () => <GenericFailureModal error={err} />,
                });
                reject(err);
              },
            },
          ),
        );
      }}
    />
  );
};

export default RemoveUserFromGroupModal;
